import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { styled } from "@pigment-css/react";

const Accordion = styled("div")(({ theme }) => ({
  overflow: "hidden",
  transition: `height ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}, opacity ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.standard}`,
}));

const AccordionInner = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  // Owns the calendar's top + side inset now that the card's padding is gone; the bottom inset
  // is supplied by the trigger row's own top padding below.
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]} 0`,
}));

interface ExpandableAccordionProps {
  calendarExpanded: boolean;
  /** The sheet's scrollable Body. While the height animates, it's pinned to the bottom so the
   * accordion visibly grows *upward*: the trigger row below stays put under the finger and earlier
   * content scrolls up past the sheet's `maxHeight` cap. A no-op on tall panes where the expanded
   * sheet still fits (scrollHeight === clientHeight, so pinning to the bottom keeps scrollTop at 0). */
  scrollRef?: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

export const ExpandableAccordion = ({ calendarExpanded, scrollRef, children }: ExpandableAccordionProps) => {
  // The calendar is always mounted (so collapsing animates too); measure its natural
  // height so the accordion can animate height 0 ↔ px. A ResizeObserver keeps the value
  // correct if the content reflows (locale/font changes). Observing the inner content
  // (not the height-controlled wrapper) avoids a measure→resize feedback loop.
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarHeight, setCalendarHeight] = useState(0);

  useLayoutEffect(() => {
    const el = calendarRef.current;
    if (!el) return;
    // Round the fractional layout height up so a sub-pixel content height can never
    // clip the trailing divider / bottom padding when the accordion is expanded.
    const measure = () => setCalendarHeight(Math.ceil(el.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Keep the scroll area pinned to the bottom for the duration of the height transition (see
  // `scrollRef`). Skips the initial mount — there's no transition there, and pinning would snap a
  // freshly opened sheet to the bottom instead of leaving the focused description field in view.
  const accordionRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  useLayoutEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const scroller = scrollRef?.current;
    const accordion = accordionRef.current;
    if (!scroller || !accordion) return;

    let frame = 0;
    const pinToBottom = () => {
      // scrollTop clamps to the max, so this is a no-op until the content actually overflows.
      scroller.scrollTop = scroller.scrollHeight;
      frame = requestAnimationFrame(pinToBottom);
    };
    frame = requestAnimationFrame(pinToBottom);

    const stop = () => cancelAnimationFrame(frame);
    const handleEnd = (event: TransitionEvent) => {
      if (event.propertyName === "height") stop();
    };
    accordion.addEventListener("transitionend", handleEnd);
    // Safety net: stop pinning even if no `transitionend` fires (e.g. the height didn't change).
    const fallback = window.setTimeout(stop, 400);

    return () => {
      stop();
      window.clearTimeout(fallback);
      accordion.removeEventListener("transitionend", handleEnd);
    };
  }, [calendarExpanded, scrollRef]);

  // Kept mounted so collapsing animates too.
  return (
    <Accordion
      ref={accordionRef}
      style={{ height: calendarExpanded ? calendarHeight : 0, opacity: calendarExpanded ? 1 : 0 }}
    >
      <AccordionInner ref={calendarRef}>{children}</AccordionInner>
    </Accordion>
  );
};
